import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import queryKeys from '@/constants/queryKeys';
import {
  likeContent,
  markContentNotInterested,
  reportContent,
  saveContent,
  shareContent,
  unlikeContent,
  unmarkContentNotInterested,
  unsaveContent,
} from '@/services/api/requests/content';
import { TContent, TContentReportReason } from '@/types/Content.types';
import {
  applyLike,
  applySave,
  applyShare,
  invalidateFocusedFeed,
  patchContentInCaches,
  removeContentFromBrowseCaches,
  restoreContentToBrowseCaches,
} from '@/utils/contentCache';
import { TCustomAxiosError } from '@/services/api';

// One shared reactions module for every surface — grid card, detail page and the focused
// loop's right rail all drive off the same `viewerState`, so a like in one place is
// immediately reflected in the others.
//
// Each mutation patches the item across all content caches optimistically and reverses
// that exact patch if the request fails, so a failed toggle can't leave the UI lying.
const useContentReactions = (content: TContent) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { id, viewerState } = content;
  const liked = viewerState?.liked ?? false;
  const saved = viewerState?.saved ?? false;

  const failed = useCallback(
    (title: string) => toast({ status: 'error', title, duration: 2500, isClosable: true }),
    [toast],
  );

  const likeMutation = useMutation({
    mutationFn: (next: boolean) => (next ? likeContent(id) : unlikeContent(id)),
    onMutate: (next: boolean) => {
      patchContentInCaches(queryClient, id, applyLike(next));
    },
    onError: (_error, next) => {
      patchContentInCaches(queryClient, id, applyLike(!next));
      failed('Couldn’t update your reaction');
    },
  });

  const saveMutation = useMutation({
    mutationFn: (next: boolean) => (next ? saveContent(id) : unsaveContent(id)),
    onMutate: (next: boolean) => {
      patchContentInCaches(queryClient, id, applySave(next));
    },
    onError: (_error, next) => {
      patchContentInCaches(queryClient, id, applySave(!next));
      failed('Couldn’t update your saved items');
    },
    // Saving changes list membership, not just a flag, so the saved list must refetch.
    onSettled: () => queryClient.invalidateQueries({ queryKey: [queryKeys.content.saved] }),
  });

  const shareMutation = useMutation({
    mutationFn: (channel: string) => shareContent(id, channel),
    onMutate: () => {
      patchContentInCaches(queryClient, id, applyShare());
    },
    // The response carries the authoritative count, so replace the optimistic guess.
    onSuccess: (response) => {
      if (typeof response?.shareCount === 'number') {
        patchContentInCaches(queryClient, id, (item) => ({
          ...item,
          stats: { ...item.stats, shareCount: response.shareCount },
        }));
      }
    },
    onError: () => {
      patchContentInCaches(queryClient, id, (item) => ({
        ...item,
        stats: { ...item.stats, shareCount: Math.max(0, (item.stats?.shareCount ?? 1) - 1) },
      }));
    },
  });

  const reportMutation = useMutation({
    mutationFn: ({ reason, note }: { reason: TContentReportReason; note?: string }) => reportContent(id, reason, note),
    onError: (e: TCustomAxiosError) =>
      toast({
        status: 'error',
        title: e.errors?.[0]?.message ?? 'Couldn’t submit your report',
        duration: 2500,
        isClosable: true,
        position: 'top',
      }),
  });

  // The backend permanently hides a not-interested item from the requester across every
  // feed surface on the next read (and DELETE restores it). On a successful mark we
  // optimistically drop it from the browse caches so it vanishes immediately.
  //
  // The flag lives here (not the component) so a rejected request can actually undo it — a
  // component holding its own state would keep showing "marked" after the backend refused,
  // which is the UI telling the user something untrue.
  //
  // Seeded from the server's viewer state so a durable mark survives a reload / direct
  // link. `notInterested` isn't in the generated ContentViewerStateDto yet — read
  // defensively so this becomes accurate the moment the backend adds it (until then it's
  // always false, matching today's session-only behaviour).
  const [isNotInterested, setIsNotInterested] = useState(() =>
    Boolean((content.viewerState as { notInterested?: boolean } | undefined)?.notInterested),
  );

  const notInterestedMutation = useMutation({
    mutationFn: (next: boolean) => (next ? markContentNotInterested(id) : unmarkContentNotInterested(id)),
    onMutate: (next: boolean) => setIsNotInterested(next),
    onSuccess: (_data, next) => {
      // Cache changes on success only — restoring a removed item to its exact old feed
      // position on failure isn't feasible, so nothing is mutated speculatively.
      if (next) {
        removeContentFromBrowseCaches(queryClient, id); // mark → drop from browse now
      } else {
        restoreContentToBrowseCaches(queryClient); // unmark → bring it back
      }
      // Either way the focused feed's peer/replay suppression changed; re-open picks it up.
      invalidateFocusedFeed(queryClient);
    },
    onError: (_error, next) => {
      setIsNotInterested(!next);
      failed('Couldn’t record that');
    },
  });

  const { mutate: mutateLike } = likeMutation;
  const { mutate: mutateSave } = saveMutation;
  const { mutateAsync: shareAsync } = shareMutation;

  const toggleLike = useCallback(() => mutateLike(!liked), [mutateLike, liked]);
  const toggleSave = useCallback(() => mutateSave(!saved), [mutateSave, saved]);

  // Records the share first, then copies the URL the backend returns.
  //
  // The order matters: the backend mints the canonical share URL, which need not be the
  // page the user is currently on (it may be public, share-safe or tracked, where an
  // in-app URL would be none of those). `fallbackUrl` is used only if that call fails, so
  // the copy action still does something useful when the API is down.
  const share = useCallback(
    async (fallbackUrl: string, channel = 'copy_link') => {
      let url = fallbackUrl;

      try {
        const response = await shareAsync(channel);
        if (response?.shareUrl) url = response.shareUrl;
      } catch {
        // Keep the fallback; the mutation's own onError already rolled back the count.
      }

      try {
        await navigator.clipboard.writeText(url);
        toast({ status: 'success', title: 'Link copied', duration: 2000, isClosable: true });
      } catch {
        failed('Couldn’t copy link');
      }
    },
    [failed, shareAsync, toast],
  );

  return {
    liked,
    saved,
    likeCount: content.stats?.likeCount ?? 0,
    saveCount: content.stats?.saveCount ?? 0,
    shareCount: content.stats?.shareCount ?? 0,
    toggleLike,
    toggleSave,
    share,
    report: reportMutation.mutateAsync,
    isReporting: reportMutation.isPending,
    isNotInterested,
    toggleNotInterested: () => notInterestedMutation.mutate(!isNotInterested),
    markNotInterested: () => notInterestedMutation.mutate(true),
    undoNotInterested: () => notInterestedMutation.mutate(false),
    isNotInterestedPending: notInterestedMutation.isPending,
  };
};

export default useContentReactions;
